import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import BrandMark from './BrandMark';
import ThemeToggle from './ThemeToggle';
import LangSwitch from './LangSwitch';
import ProductUpdates from './ProductUpdates.jsx';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { useDashboardData } from '../../state/DashboardDataContext';
import { effectiveAccessState } from '../../lib/billingState.js';
import { brandKitEnabled } from '../../lib/flags.js';
import { useFreePreview } from '../../contexts/FreePreviewContext.jsx';
import { freePublishingState } from '../../lib/freePublishing.js';
import { postingLockedSection } from '../../lib/postingAccess.js';
import { api } from '../../services/api/client.js';
import { BRAND_CREATED_EVENT } from '../../lib/quickBrand.js';
import { PRIMARY_LOGO_UPDATED_EVENT } from '../../lib/brandLogo.js';

// Backend plan_key → human label for the sidebar account chip.
const PLAN_LABEL = {
  brand_pilot: 'Brand Pilot',
  marketing_operator: 'Marketing Operator',
  multi_brand: 'Multi-Brand',
  enterprise: 'Enterprise',
  starter: 'Brand Pilot',
  growth: 'Marketing Operator',
  pro: 'Multi-Brand',
  business: 'Multi-Brand',
  pilot_growth: 'Brand Pilot',
  growth_plus: 'Marketing Operator',
  free: 'Free'
};

// Marketing / Research hub visibility — now GLOBAL (default ON). It shows for
// everyone unless explicitly disabled with localStorage 'dathent.research.
// enabled' = '0' (an emergency off-switch). It used to be opt-in ('1') during
// the gradual rollout. Research ACCESS is still plan-gated on the backend
// (research_runs_monthly, free = 0); this only controls nav/hub visibility.
// Read live (the SPA never reloads mid-session).
function researchNavEnabled() {
  try {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('dathent.research.enabled') !== '0';
  } catch (_) {
    return true;
  }
}

// "Create post" is intentionally NOT a nav item — the prominent "+ New post"
// CTA button directly above the nav is the single create entry point (the old
// duplicate nav row pointed at the same /app/create).
const NAV = [
  { id: 'dashboard', labelKey: 'navDashboard', Icon: I.home, to: '/app' },
  { id: 'calendar', labelKey: 'navCalendar', Icon: I.calendar, to: '/app/calendar' },
  { id: 'library', labelKey: 'navLibrary', Icon: I.library, to: '/app/library' },
  { id: 'plan', labelKey: 'navContentPlan', Icon: I.target, to: '/app/plan' },
];

// With the research flags on, Research and Content Plan collapse into one
// "Marketing" hub entry — the in-page sub-nav (MarketingSubnav) switches
// between the surfaces (Research · Content Plan · Brand Kit).
const MARKETING_NAV = { id: 'marketing', labelKey: 'navMarketing', Icon: I.target, to: '/app/plan' };
// Brand Kit promoted to a top-level "Media" button (flag-gated). It ALSO stays
// as a tab inside the Marketing hub (MarketingSubnav) — reachable from both.
const MEDIA_NAV = { id: 'media', labelKey: 'navMedia', Icon: I.image, to: '/app/brandkit' };

function navItems() {
  const base = researchNavEnabled() ? [...NAV.slice(0, 3), MARKETING_NAV] : NAV;
  return brandKitEnabled() ? [...base, MEDIA_NAV] : base;
}

/**
 * Map a route pathname to the active sidebar nav id.
 * Exported so the Topbar / AppShell can mirror current section if needed.
 */
export function navIdForPath(pathname) {
  if (pathname === '/app' || pathname === '/app/') return 'dashboard';
  if (pathname.startsWith('/app/create')) return 'create-post';
  if (pathname.startsWith('/app/calendar')) return 'calendar';
  if (pathname.startsWith('/app/library')) return 'library';
  if (pathname.startsWith('/app/research')) return researchNavEnabled() ? 'marketing' : 'research';
  if (pathname.startsWith('/app/brandkit')) return brandKitEnabled() ? 'media' : (researchNavEnabled() ? 'marketing' : null);
  if (pathname.startsWith('/app/plan')) return researchNavEnabled() ? 'marketing' : 'plan';
  return null;
}

// Small inline glyphs the shared icon set doesn't carry.
const LockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>
);
const ChevDown = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
);
// Discord brand mark is a FILLED shape, so it can't live in the stroke-based
// NAV_GLYPH set — rendered as its own component (nominative use: links to our
// own community server, same invite as the landing widget/footer).
const DiscordIcon = ({ size = 19 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286ZM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189Zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" /></svg>
);
const ExternalArrow = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
);

// Exact nav glyphs from the sidebar pack (so the rail reads as the designed set,
// not the generic app icons). Keyed by nav id; `Glyph` renders one at any size.
const NAV_GLYPH = {
  dashboard: <><path d="M3 9.5 12 3l9 6.5V21H3z" /><path d="M9 21v-7h6v7" /></>,
  calendar: <><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></>,
  library: <path d="M4 19V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v14M4 19a2 2 0 0 0 2 2h13M4 19a2 2 0 0 1 2-2h13" />,
  marketing: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /></>,
  plan: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /></>,
  media: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></>,
  referrals: <><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13" /><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" /><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" /></>,
  settings: <><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></>,
  help: <><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></>,
};
const Glyph = ({ id, size = 19 }) => (
  // strokeLinecap round is what makes the help "?" dot (a zero-length "h.01"
  // segment) render as a visible round dot — without it the dot is invisible.
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{NAV_GLYPH[id] || NAV_GLYPH.dashboard}</svg>
);

export default function Sidebar({ mobileOpen, onMobileClose, panelRef, mobileMenuButtonRef }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const current = navIdForPath(pathname);
  const { user, logout } = useAuth?.() || {};
  const userLabel = user?.name || user?.email || t('sidebar.account');
  const dash = useDashboardData?.() || {};
  const entitlements = dash.entitlements;
  const postingPlan = entitlements?.plan === 'posting';
  const freePreview = useFreePreview();
  const ownPublishing = Boolean(freePublishingState(dash.entitlementsError ? null : entitlements || freePreview.entitlements));
  const accessState = effectiveAccessState(entitlements);
  // Returned-Free accounts can retain historical lapsed billing. Use the
  // fresh capability for display only, never the preview fallback or plan name.
  const planLabel = ['free', 'new', 'lapsed'].includes(accessState) && !dash.entitlementsError && freePublishingState(entitlements)
    ? t('settings.planFree')
    : accessState === 'lapsed'
    ? t('settings.inactiveAccess')
    : accessState === 'free' && entitlements.effectivePlan === 'free' && !dash.entitlementsError && !entitlements.freePublishing ? t('settings.planFree')
    : accessState && entitlements.plan !== 'free' ? (entitlements.planName || PLAN_LABEL[entitlements.plan] || null) : null;

  // The active brand for the (locked) brand chip. Multibrand isn't real yet, so
  // this is simply the user's single brand — shown to make the locked switcher
  // feel concrete + desirable. Best-effort, cached (5-min TTL in the client).
  const [brandName, setBrandName] = useState('');
  const [brandLogoUrl, setBrandLogoUrl] = useState(null);
  const logoUpdated = useRef(false);
  useEffect(() => {
    let alive = true;
    api.profile.getBrandProfile()
      .then((res) => {
        const bp = (res?.data ?? res)?.brandProfile || null;
        const nm = (bp?.businessName || '').trim();
        if (alive) {
          if (nm) setBrandName(nm);
          if (!logoUpdated.current) setBrandLogoUrl(bp?.logoUrl || null);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    const updateCreatedBrand = (event) => {
      const name = String(event?.detail?.name || '').trim();
      if (name) setBrandName(name);
    };
    window.addEventListener(BRAND_CREATED_EVENT, updateCreatedBrand);
    return () => window.removeEventListener(BRAND_CREATED_EVENT, updateCreatedBrand);
  }, []);
  useEffect(() => {
    const updateLogo = (event) => {
      logoUpdated.current = true;
      setBrandLogoUrl(event?.detail?.url || null);
    };
    window.addEventListener(PRIMARY_LOGO_UPDATED_EVENT, updateLogo);
    return () => window.removeEventListener(PRIMARY_LOGO_UPDATED_EVENT, updateLogo);
  }, []);
  const brandDisplay = brandName || t('sidebar.yourBrand');
  const brandMono = (brandName || 'B').trim().charAt(0).toUpperCase();

  // Multi-brand: the LIVE switcher (dropdown) renders only when the flag is on
  // AND the plan is multi-brand-capable; otherwise the shipped LOCKED chip +
  // upsell below render exactly as before. The chip shows the active brand.
  const brand = useBrand();
  const liveSwitcher = brand.enabled && brand.canMultiBrand;
  const chipName = liveSwitcher ? (brand.activeBrand?.name || brandDisplay) : brandDisplay;
  const chipMono = liveSwitcher ? (brand.activeBrand?.monogram || brandMono) : brandMono;
  const chipLogo = liveSwitcher ? (brand.activeBrand?.logoUrl || null)
    : logoUpdated.current ? brandLogoUrl : (brand.activeBrand?.logoUrl || brandLogoUrl);
  const [switchToast, setSwitchToast] = useState(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const menuRef = useRef(null);
  const brandRef = useRef(null);

  // Desktop collapse to an icon-only rail (persisted). On mobile the off-canvas
  // drawer owns this, so the toggle is hidden there via CSS.
  const [collapsed, setCollapsed] = useState(() => {
    try { return typeof window !== 'undefined' && window.localStorage.getItem('dathent.sb.collapsed') === '1'; } catch { return false; }
  });
  const toggleCollapse = () => setCollapsed((c) => {
    const next = !c;
    try { window.localStorage.setItem('dathent.sb.collapsed', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  useEffect(() => {
    if (!menuOpen && !brandOpen) return undefined;
    const onDoc = (e) => {
      if (menuOpen && !menuRef.current?.contains(e.target)) setMenuOpen(false);
      if (brandOpen && !brandRef.current?.contains(e.target)) setBrandOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setMenuOpen(false); setBrandOpen(false); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [menuOpen, brandOpen]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    try {
      await logout?.();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleSwitch = (b) => {
    setBrandOpen(false);
    if (!b || b.id === brand.activeBrandId) return;
    brand.switchBrand(b.id);
    setSwitchToast({ name: b.name || t('sidebar.yourBrand'), mono: (b.monogram || 'B'), logo: b.logoUrl || null });
  };

  // Auto-dismiss the "Now working on X" toast.
  useEffect(() => {
    if (!switchToast) return undefined;
    const id = setTimeout(() => setSwitchToast(null), 2800);
    return () => clearTimeout(id);
  }, [switchToast]);

  return (
    <aside ref={panelRef} id="app-navigation" className={`sidebar ${collapsed ? 'is-rail' : ''}`}
      role={mobileOpen ? 'dialog' : undefined} aria-modal={mobileOpen ? true : undefined}
      aria-label={mobileOpen ? t('topbar.openMenu') : undefined}
      onClick={event => { if (mobileOpen && event.target.closest('a[href]')) onMobileClose?.(); }}>
      <div className="sb-head">
        <Link to="/app" className="sb-brand">
          <BrandMark size={26} withText={true} />
        </Link>
        {/* Desktop collapse-to-rail toggle (hidden on mobile via CSS). */}
        <button
          type="button"
          className="sb-collapse"
          onClick={toggleCollapse}
          aria-label={t(collapsed ? 'sidebar.expand' : 'sidebar.collapse')}
          title={t(collapsed ? 'sidebar.expand' : 'sidebar.collapse')}
        >
          {collapsed
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="m9 18 6-6-6-6" /></svg>
            : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16" /><path d="m14 9-2 3 2 3" strokeWidth="1.7" /></svg>}
        </button>
        {/* Mobile-only drawer close (hidden on desktop via CSS). */}
        <button
          type="button"
          className="sb-close"
          onClick={onMobileClose}
          aria-label={t('sidebar.closeNavigation')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
      </div>

      {/* Brand switcher. Live dropdown when the plan is multi-brand-capable AND
          the flag is on; otherwise the shipped LOCKED chip + upsell. */}
      <div className="sb-top" ref={brandRef}>
        <button
          type="button"
          className={`sb-brandchip ${brandOpen ? 'open' : ''}`}
          onClick={() => setBrandOpen((o) => !o)}
          aria-haspopup={liveSwitcher ? 'menu' : 'dialog'}
          aria-expanded={brandOpen}
        >
          <span className={`btile${chipLogo ? ' has-logo' : ''}`}>{chipLogo ? <img src={chipLogo} alt="" /> : chipMono}</span>
          <span className="sb-bn">
            <span className="k">{t('sidebar.workingAs')}</span>
            <span className="nm">{chipName}</span>
          </span>
          {liveSwitcher
            ? <span className="sb-chev"><ChevDown size={15} /></span>
            : !brandName && <span className="sb-lock" title={t('sidebar.multibrandLockTitle')}><LockIcon /></span>}
          <span className="sr-only">{t('sidebar.switchBrand')}</span>
        </button>
        {brandOpen && liveSwitcher && (
          <div className="bdrop" role="menu" aria-label={t('sidebar.yourBrands', { defaultValue: 'Your brands' })}>
            <div className="bd-h">{t('sidebar.yourBrands', { defaultValue: 'Your brands' })}</div>
            <div className="bd-list">
            {brand.brands.map((b) => {
              const isActive = b.id === brand.activeBrandId;
              return (
                <button
                  key={b.id}
                  type="button"
                  className={`bd-row ${isActive ? 'active' : ''}`}
                  role="menuitemradio"
                  aria-checked={isActive}
                  onClick={() => handleSwitch(b)}
                >
                  <span className={`btile${b.logoUrl ? ' has-logo' : ''}`}>{b.logoUrl ? <img src={b.logoUrl} alt="" /> : (b.monogram || 'B')}</span>
                  <span className="bd-nm">
                    <span className="t">{b.name || t('sidebar.yourBrand')}</span>
                    <span className="h">{b.hasStrategy
                      ? t('sidebar.brandStrategyReady', { defaultValue: 'Strategy ready' })
                      : t('sidebar.brandNoResearch', { defaultValue: 'No research yet' })}</span>
                  </span>
                  {isActive && <span className="bd-state">{t('sidebar.brandActive', { defaultValue: 'Active' })}</span>}
                </button>
              );
            })}
            </div>
            <div className="bd-sep" />
            <button
              type="button"
              className="bd-add"
              onClick={() => {
                setBrandOpen(false);
                onMobileClose?.({ restoreFocus: false });
                brand.openAddBrand?.();
              }}
              disabled={brand.atLimit}
            >
              <span className="ic"><I.plus size={14} /></span>
              {t('sidebar.addBrand')}
            </button>
          </div>
        )}
        {brandOpen && !liveSwitcher && (
          <div className="upsell" role="dialog" aria-label={t('sidebar.multibrandTitle')}>
            <div className="up-top">
              <span className="up-badge"><LockIcon size={10} />{t('sidebar.multibrand')}</span>
              <h4>{t('sidebar.multibrandTitle')}</h4>
              <p>{t('sidebar.multibrandBody')}</p>
            </div>
            <div className="up-preview">
              <span className="pv"><span className="btile sm">{brandMono}</span><span className="pn">{brandDisplay}</span></span>
              <span className="pv ghost"><span className="btile sm">+</span><span className="pn">{t('sidebar.addBrand')}</span></span>
              <span className="pv ghost"><span className="btile sm">+</span><span className="pn">{t('sidebar.addBrand')}</span></span>
            </div>
            <div className="up-foot">
              <div className="up-note">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></svg>
                {t('sidebar.multibrandNote')}
              </div>
              <button type="button" className="btn-up" onClick={() => { setBrandOpen(false); navigate('/app/settings?tab=billing'); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z" /></svg>
                {t('sidebar.multibrandCta')}
              </button>
            </div>
          </div>
        )}
      </div>

      <Link to="/app/create" className="sb-cta">
        <span className="pl"><I.plus size={14} /></span>
        <span className="lbl">{t('sidebar.newPost')}</span>
        {freePreview.active && !ownPublishing
          ? <span className="sb-lock" title={t('freePreview.navLockedHint')}><LockIcon size={11} /></span>
          : <span className="kbd">N</span>}
      </Link>

      <nav className="sb-nav" aria-label="Primary">
        {navItems().map((item) => (
          <NavLink
            key={item.id}
            data-nav-id={item.id}
            to={item.to}
            end={item.to === '/app'}
            className={({ isActive }) => `sb-link ${isActive || current === item.id ? 'is-active' : ''}`}
            aria-current={current === item.id ? 'page' : undefined}
            title={collapsed ? t(`sidebar.${item.labelKey}`) : undefined}
          >
            <span className="gi"><Glyph id={item.id} size={19} /></span>
            <span>{t(`sidebar.${item.labelKey}`)}</span>
            {postingLockedSection({ pathname: item.to, entitlements: dash.entitlementsError ? null : entitlements || freePreview.entitlements }) && (
              <span className="sb-lock" style={{ display: 'inline-flex' }} title={t('postingWorkspace.lockTitle')} aria-label={t('postingWorkspace.lockTitle')}><LockIcon size={11} /></span>
            )}
            {/* GH-1133 S5 (§2.8): free-preview locked sections open S10. */}
            {!postingPlan && !ownPublishing && freePreview.active && ['calendar', 'plan', 'marketing'].includes(item.id) && (
              <span className="sb-lock" style={{ display: 'inline-flex' }} title={t('freePreview.navLockedHint')} aria-label={t('freePreview.navLockedHint')}><LockIcon size={11} /></span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="sb-sep" />

      <nav className="sb-nav" aria-label="Secondary">
        <NavLink to="/app/referrals" className={({ isActive }) => `sb-link sb-link-sm ${isActive ? 'is-active' : ''}`} title={collapsed ? t('sidebar.referrals') : undefined}>
          <span className="gi"><Glyph id="referrals" /></span>
          <span>{t('sidebar.referrals')}</span>
        </NavLink>
        <NavLink to="/app/settings" className={({ isActive }) => `sb-link sb-link-sm ${isActive ? 'is-active' : ''}`} title={collapsed ? t('sidebar.settings') : undefined}>
          <span className="gi"><Glyph id="settings" /></span>
          <span>{t('sidebar.settings')}</span>
        </NavLink>
        {user?.id && <ProductUpdates key={user.id} userId={user.id} onMobileClose={onMobileClose} mobileMenuButtonRef={mobileMenuButtonRef} />}
        <NavLink to="/app/help" className={({ isActive }) => `sb-link sb-link-sm ${isActive ? 'is-active' : ''}`} title={collapsed ? t('sidebar.helpCenter') : undefined}>
          <span className="gi"><Glyph id="help" /></span>
          <span>{t('sidebar.helpCenter')}</span>
        </NavLink>
        <a
          href="https://discord.gg/YN4g3ThGqM"
          target="_blank"
          rel="noopener noreferrer"
          className="sb-link sb-link-sm"
          title={collapsed ? t('sidebar.discord') : undefined}
        >
          <span className="gi"><DiscordIcon /></span>
          <span>{t('sidebar.discord')}</span>
          <span className="sb-ext"><ExternalArrow /></span>
        </a>
      </nav>

      <div className="sb-foot" ref={menuRef}>
        <button
          type="button"
          className={`sb-user ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={collapsed ? userLabel : undefined}
          title={collapsed ? planLabel : userLabel}
        >
          <span className="sb-user-av">{(user?.name || user?.email || 'U').slice(0, 1).toUpperCase()}</span>
          <span className="sb-user-text">
            <span className="sb-user-name">{userLabel}</span>
            <span className="sb-user-sub">{planLabel && <span className="planpill">{planLabel}</span>}</span>
          </span>
          <span className="chev"><ChevDown /></span>
        </button>
        {menuOpen && (
          <div className="acctmenu" role="menu">
            <div className="mhead">
              <span className="av">{(user?.name || user?.email || 'U').slice(0, 1).toUpperCase()}</span>
              <div>
                <div className="nm">{user?.name || t('sidebar.account')}</div>
                {user?.email && <div className="em">{user.email}</div>}
                {planLabel && <span className="planpill">{planLabel}</span>}
              </div>
            </div>
            <button type="button" className="mi" onClick={() => go('/app/settings?tab=profile')}>
              <I.user size={16} /> <span>{t('sidebar.profile')}</span>
            </button>
            <button type="button" className="mi" onClick={() => go('/app/settings?tab=billing')}>
              <I.zap size={16} /> <span>{t('sidebar.planAndBilling')}</span>
            </button>
            <button type="button" className="mi" onClick={() => go('/app/settings?tab=accounts')}>
              <I.share size={16} /> <span>{t('sidebar.connectedAccounts')}</span>
            </button>
            <button type="button" className="mi" onClick={() => go('/app/help')}>
              <I.info size={16} /> <span>{t('sidebar.helpCenterMenu')}</span>
            </button>
            {user?.isAdmin && (
              <>
                {/*
                  Admin is a static HTML page outside the SPA (so we hard-
                  navigate instead of router.push). The link only renders for
                  users flagged `isAdmin` by /api/auth/me, but the backend
                  re-enforces admin gating on every /api/admin/* endpoint.
                */}
                <a className="mi admin" href="/admin" rel="noopener" onClick={() => setMenuOpen(false)}>
                  <I.users size={16} /> <span>{t('sidebar.admin')}</span>
                </a>
              </>
            )}
            <div className="midiv" />
            <button type="button" className="mi danger" onClick={handleSignOut}>
              <I.logout size={16} /> <span>{t('sidebar.signOut')}</span>
            </button>
          </div>
        )}
      </div>

      {/* Mobile-only drawer footer (mobile shell fix, 2026-09): Theme +
          Language controls move here from the topbar to free up room there
          for the ChatGPT pill + bell. Always rendered (like .sb-close) —
          hidden by default via CSS, shown only inside the ≤768px mobile
          block in shell.css, so desktop keeps using the topbar's copies. */}
      <div className="sb-mobile-tools">
        <ThemeToggle />
        <LangSwitch />
      </div>

      {/* Switch confirmation toast — portaled to body so the mobile drawer's
          transform doesn't become its containing block (fixed positioning). */}
      {switchToast && createPortal(
        <div className="btoast" role="status" aria-live="polite">
          <span className={`btile${switchToast.logo ? ' has-logo' : ''}`}>{switchToast.logo ? <img src={switchToast.logo} alt="" /> : switchToast.mono}</span>
          <span className="tx">
            <span className="t">{t('sidebar.nowWorkingOn', { defaultValue: 'Now working on' })} <b>{switchToast.name}</b></span>
            <span className="s">{t('sidebar.switchToastSub', { defaultValue: 'Marketing and library now reflect this brand.' })}</span>
          </span>
        </div>,
        document.body
      )}
    </aside>
  );
}
